import { useEffect, useMemo, useState } from 'react';
import { getCustomAdAgreeYn } from '@wconcept/wconcept-common/utils/cookie';
import { Product } from '@wconcept/wconcept-common/models/display/product';
import { MolocoAdProductsResult } from '@wconcept/wconcept-common/models/display/search';
import { useMergedProductsWithAds } from '@hooks/useMergedProductsWithAds';
import { MolocoAdLPBaseData, MolocoAdLPProduct } from '@models/moloco';
import {
  getLpProductListColumnPosition,
  type LpProductListCols,
} from '@wconcept/wconcept-common/utils/lpProductListCols';
import { VirtualizedItem } from './types';

export const useCustomAd = () => {
  const [isCustomAd, setIsCustomAd] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setIsCustomAd((await getCustomAdAgreeYn()) !== 'N');
      } catch (error) {
        console.error('Failed to get custom ad agreement:', error);
        setIsCustomAd(false);
      }
    })();
  }, []);

  return isCustomAd;
};

export const useVirtualizedItems = (
  products: Product[],
  isCustomAd: boolean,
  isMobile: boolean,
  molocoAdLpProducts?: MolocoAdLPProduct[],
  molocoAdLpBaseData?: MolocoAdLPBaseData,
  molocoAdProductsResult?: MolocoAdProductsResult,
  totalElements?: number,
  mobileColumnCount: LpProductListCols = 2
): VirtualizedItem[] => {
  const mergedItems = useMergedProductsWithAds({
    products,
    molocoProducts: molocoAdLpProducts,
    molocoAdLpBaseData,
    isMobile,
    mobileColumnCount,
  });

  return useMemo(() => {
    const shouldInsertAd =
      isCustomAd &&
      molocoAdProductsResult &&
      !!totalElements &&
      totalElements > 0 &&
      isMobile;

    if (!shouldInsertAd) {
      return mergedItems;
    }

    const webPosition = getLpProductListColumnPosition(
      molocoAdProductsResult.webPosition,
      molocoAdProductsResult.webThreeColumnPosition,
      mobileColumnCount
    );
    const result = [...mergedItems];
    // 상품이 6개 이상이면 webPosition 기준 삽입,
    // 6개 미만(1~5개)이면 맨 밑에 삽입.
    const insertIndex = totalElements > 5 ? webPosition : result.length;
    if (insertIndex === undefined) {
      return mergedItems;
    }

    result.splice(insertIndex, 0, {
      type: 'ad',
      adData: molocoAdProductsResult,
    });

    return result;
  }, [
    mergedItems,
    isCustomAd,
    isMobile,
    molocoAdProductsResult,
    totalElements,
    mobileColumnCount,
  ]);
};

const DEFAULT_MOBILE_VIRTUAL_COLUMNS = 2;

export const useGridItems = (
  virtualizedItems: VirtualizedItem[],
  mobileColumnCount: number = DEFAULT_MOBILE_VIRTUAL_COLUMNS
): VirtualizedItem[][] => {
  const columnCount = Math.max(1, mobileColumnCount);

  return useMemo(() => {
    const groups: VirtualizedItem[][] = [];
    let currentGroup: VirtualizedItem[] = [];

    const FULL_WIDTH_TYPES = new Set(['ad', 'displayAd', 'styleClip']);

    virtualizedItems.forEach(item => {
      if (FULL_WIDTH_TYPES.has(item.type)) {
        if (currentGroup.length > 0) {
          groups.push([...currentGroup]);
          currentGroup = [];
        }
        groups.push([item]);
      } else {
        currentGroup.push(item);
        if (currentGroup.length === columnCount) {
          groups.push([...currentGroup]);
          currentGroup = [];
        }
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [virtualizedItems, columnCount]);
};
